import { AlexaVisibilityQuestion } from '@blisspointmedia/bpm-types/dist/AlexaVisibility';
import { DraftQuestion, MAX_QUESTIONS } from './alexa-visibility-questions-constants';

export const toDraftRows = (questions: AlexaVisibilityQuestion[]): DraftQuestion[] =>
  questions.map((question, index) => ({ ...question, rowId: `existing-${index}` }));

const trimmedOrUndefined = (value: string | undefined): string | undefined =>
  value?.trim() || undefined;

/** Normalizes editable rows into the payload shape: trimmed text, blank optionals dropped. */
export const fromDraftRows = (rows: DraftQuestion[]): AlexaVisibilityQuestion[] =>
  rows.map(row => ({
    query: row.query.trim(),
    category: trimmedOrUndefined(row.category),
    persona: trimmedOrUndefined(row.persona),
    querySet: row.querySet,
    enabled: row.enabled
  }));

const serialize = (question: AlexaVisibilityQuestion): string =>
  JSON.stringify([
    question.query,
    question.category ?? '',
    question.persona ?? '',
    question.querySet,
    question.enabled
  ]);

/** True when the draft differs from the saved list in content or order. */
export const isDraftChanged = (
  initial: AlexaVisibilityQuestion[],
  rows: DraftQuestion[]
): boolean => {
  const draft = fromDraftRows(rows);
  return (
    draft.length !== initial.length || draft.some((q, i) => serialize(q) !== serialize(initial[i]))
  );
};

/** Human-readable problems that would make the server reject the draft. */
export const getDraftErrors = (rows: DraftQuestion[]): string[] => {
  const errors: string[] = [];
  if (rows.length > MAX_QUESTIONS) {
    errors.push(`At most ${MAX_QUESTIONS} questions are allowed (currently ${rows.length}).`);
  }
  const seen = new Map<string, number>();
  fromDraftRows(rows).forEach((question, index) => {
    const rowNumber = index + 1;
    if (!question.query) {
      errors.push(`Question ${rowNumber} is empty.`);
      return;
    }
    const key = question.query.toLowerCase();
    const firstRow = seen.get(key);
    if (firstRow === undefined) {
      seen.set(key, rowNumber);
    } else {
      errors.push(`Question ${rowNumber} duplicates question ${firstRow}.`);
    }
  });
  return errors;
};

/** How many enabled questions each Run Scrape "Query Set" option would run. */
export const countEnabledBySet = (
  questions: AlexaVisibilityQuestion[]
): { all: number; a: number; b: number } => {
  const enabled = questions.filter(q => q.enabled);
  return {
    all: enabled.length,
    a: enabled.filter(q => q.querySet !== 'b').length,
    b: enabled.filter(q => q.querySet !== 'a').length
  };
};
